import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  cn,
  formatAmount,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { setBudgetData, setLineItemList } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useAddMonthlyDistributionMutation,
  useDeleteLineItemMutation,
  useGetBudgetById,
  useGetGlApprovalLimitByBudgetId,
  useGetLineItemsByBudgetId,
  useGetMonthDistributionByLineItemId,
  useUpdateMonthlyDistributionMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import {
  BadgeDollarSign,
  Check,
  Edit,
  MoreVertical,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";

export const autoApprovalLevelOptions = {
  YEARLY: "Yearly",
  MONTHLY: "Monthly",
  TRANSACTION: "Transaction",
};

const useLineItems = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const isMobile = useIsMobile();
  const location = useLocation();
  const isFromTolerance = location?.pathname?.includes("tolerance");
  const isMasterDataRoute = location.pathname.toLowerCase().includes(routes.masterData);
  const { id, budgetId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [rowSelected, setRowSelected] = useState<any>(null);
  const [openDistributionDialog, setOpenDistributionDialog] =
    useState<boolean>(false);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const communityId = (id || communityDetail?.communityId) as string;
  const budgetListPath = isMasterDataRoute
    ? `/${routes.cam}/${routes.masterData}/${routes.budgetMgmt}`
    : `/${routes.cam}/${routes.communities}/${communityId}/${routes.budgetMgmt}`;

  const budgetData = useSelector(
    (state: RootState) => state.currentData.budgetData
  );

  const { data: _budgetData, isLoading: isBudgetDataLoading, isError: isBudgetError, error: budgetError } =
    useGetBudgetById({
      communityId,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!communityId && !!budgetId && !budgetData,
      },
    });

  const isDraft = useMemo(
    () => _budgetData?.data?.status?.toLowerCase() == "draft",
    [_budgetData]
  );
  const { data: lineItems, isLoading: isLineItemDataLoading } =
    useGetLineItemsByBudgetId({
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!budgetId,
      },
    });

  const { data: glApprovalLimit, isLoading: isLimitLoading } =
    useGetGlApprovalLimitByBudgetId({
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!budgetId,
      },
    });

  const { data: monthlyDistributionData, refetch: refetchDistributionData } =
    useGetMonthDistributionByLineItemId({
      lineItemId: rowSelected?.budgetLineitemId as string,
      budgetId: budgetId as string,
      queryParams: {
        enabled: !!rowSelected?.budgetLineitemId,
      },
    });

    useEffect(() => {
      if (isBudgetError) {
        toast.error(
          (budgetError as any)?.response?.data?.message || 'Something went wrong'
        );
      }
    }, [isBudgetError, budgetError]);
    
  // add monthly distribution
  const { mutate: addDistributionInfo, isPending: isAddDistributionPending } =
    useAddMonthlyDistributionMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["line-items-list", budgetId],
        });
        setOpenDistributionDialog(false);
        toast.success("Line Item Added Successfully");
      },
    });

  // update monthly distribution
  const {
    mutate: updateDistributionInfo,
    isPending: isUpdateDistributionPending,
  } = useUpdateMonthlyDistributionMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["line-items-list", budgetId],
      });
      setOpenDistributionDialog(false);
      toast.success("Line Item Updated Successfully");
    },
  });

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteLineItemMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["line-items-list", budgetId],
        });
        setOpenDeleteDialog(false);
        setRowSelected(null);
        toast.success("Line Item Deleted Successfully");
      },
    });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const isArchived = useMemo(() => budgetData?.archived, [budgetData]);

  const breadcrumbDataCommunity = useMemo(() => {
    const currentPage = {
      link: "",
      name: <span className="text-primary">{"Line Items"}</span>,
    };
    const budgetCrumb = {
      link: budgetListPath,
      name: `Budget Management - ${(budgetData as any)?.budgetName || ""}`,
    };

    if (isMasterDataRoute) {
      return [budgetCrumb, currentPage];
    }

    return [
      {
        link: `/${routes.cam}/${routes.communities}`,
        name: `Community - ${(communityDetail as any)?.name || ""}`,
      },
      budgetCrumb,
      currentPage,
    ];
  }, [budgetData, budgetListPath, communityDetail, isMasterDataRoute]);

  const breadcrumbDataTolerance = useMemo(() => {
    dispatch(setBudgetData(_budgetData?.data));
    return [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.activityDashboard}/${routes.payment}/${routes.tolerance}`,
        name: (
          <span>Tolerance 360 Report (Year - {_budgetData?.data?.year})</span>
        ),
      },
      {
        link: "",
        name: (
          <span className="text-primary">{`Line Items - ${
            (_budgetData as any)?.data?.budgetName || ""
          }`}</span>
        ),
      },
    ];
  }, [_budgetData]);

  const onBackClick = () => {
    navigate(budgetListPath);
  };

  const breadcrumbData = isFromTolerance
    ? breadcrumbDataTolerance
    : breadcrumbDataCommunity;

  const columnsForVersion0 = [
    {
      accessorKey: "tolerance",
      header: <div>Tolerance</div>,
      accessorFn: (row: any) => `${String(row?.tolerance)}%` || "",
    },
    {
      accessorKey: "approvedToDate",
      header: <div>Actual</div>,
      cell: (row: any) => {
        const value = glApprovalLimit?.find(
          (data: any) => row?.row?.original?.glMasterId == data?.glMasterId
        )?.approvedToDate;

        const totalBudgetWithTolerance =
          row?.row?.original?.budgetYearlyAmt +
          (row?.row?.original?.budgetYearlyAmt *
            row?.row?.original?.tolerance) /
            100;

        return (
          <span
            className={cn(
              totalBudgetWithTolerance < value ? "text-red-600 font-[500]" : ""
            )}
          >
            {formatAmount(value)}
          </span>
        );
      },
      size: 50,
    },
    {
      accessorKey: "remainingLimit",
      header: <div>Remaining Budget</div>,
      accessorFn: (row: any) => {
        const value = glApprovalLimit?.find(
          (data: any) => row?.glMasterId == data?.glMasterId
        )?.remainingLimit;
        return formatAmount(value);
      },
      size: 50,
    },
  ];
  // Define Table Columns
  const columns = [
    {
      accessorKey: "glMasterAccountShortDesc",
      header: <div>GL Master</div>,
      accessorFn: (row: any) =>
        String(`${row?.glAccountNumber}-${row?.glMasterAccountShortDesc}`) ||
        "",
    },
    {
      accessorKey: "budgetTransactionRate",
      meta: { align: "right" },
      header: <div>Rate</div>,
      accessorFn: (row: any) =>
        formatAmount(String(row?.budgetTransactionRate) || "0"),
      size: 50,
    },
    {
      accessorKey: "budgetedQuantity",
      header: <div>Quantity</div>,
      accessorFn: (row: any) => String(row?.budgetedQuantity) || "",
      size: 50,
    },
    {
      accessorKey: "budgetYearlyAmt",
      header: <div>Annual Budget</div>,
      accessorFn: (row: any) =>
        formatAmount(String(row?.budgetYearlyAmt) || ""),
    },

    ...(budgetData?.versionNo == 0 ? columnsForVersion0 : []),
    {
      accessorKey: "autoApproval",
      header: (
        <div>
          Auto <br /> Invoice Approval
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.autoApproval ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "autoApprovalLevel",
      header: (
        <div>
          Auto Invoice
          <br /> Approval Level
        </div>
      ),
      accessorFn: (row: any) =>
        String((autoApprovalLevelOptions as any)[row?.autoApprovalLevel] || ""),
    },
    ...(isDraft
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: (row: any) => (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <MoreVertical className="w-5 h-5 cursor-pointer" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-white border-0 shadow-md"
                >
                  <DropdownMenuItem
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`${row.row.original.budgetLineitemId}/edit`);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={cn("flex items-center gap-2", row.row?.original?.archived ? "opacity-50 cursor-normal !hover:opacity-50 !hover:bg-white" : "")}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (row.row?.original?.archived) {
                        return ;
                      } else { 
                        setRowSelected(row.row.original);
                        setOpenDeleteDialog(true);
                      }
                    }}
                  >
                    {row.row?.original?.archived ? 
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger type="button">
                          <div className="flex flex-row gap-2">
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            This GL line cannot be deleted because financial documents<br/> have already been posted against it.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    : <>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </>}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setRowSelected(row.row.original);
                      setOpenDistributionDialog(true);
                    }}
                  >
                    <BadgeDollarSign className="w-4 h-4" />
                    Monthly Budget Breakup
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    dispatch(setLineItemList(lineItems?.data));
  }, [lineItems]);

  useEffect(() => {
    if (openDistributionDialog) {
      refetchDistributionData();
    }
  }, [rowSelected, openDistributionDialog]);

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  const onRowClick = (row: any) => {
    navigate(`${row.original.budgetLineitemId}/view`);
  };

  const onDeleteConfirm = () => {
    deleteInfo({
      id: rowSelected?.budgetLineitemId,
      budgetId: budgetId as string,
    });
  };

  const onSaveMonthlyDistribution = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (monthlyDistributionData?.data) {
      updateDistributionInfo({
        budgetId: budgetId as string,
        lineItemId: rowSelected?.budgetLineitemId,
        data: _data,
      });
    } else {
      addDistributionInfo({
        budgetId: budgetId as string,
        lineItemId: rowSelected?.budgetLineitemId,
        data: _data,
      });
    }
  };

  return {
    breadcrumbData,
    isMobile,
    onAdd,
    globalFilter,
    setGlobalFilter,
    onRowClick,
    columns,
    budgetData,
    rowSelected,
    onDeleteConfirm,
    openDeleteDialog,
    setOpenDeleteDialog,
    isDeletePending,
    lineItems,
    isLineItemDataLoading,
    openDistributionDialog,
    setOpenDistributionDialog,
    onSaveMonthlyDistribution,
    isUpdateDistributionPending,
    isAddDistributionPending,
    isDraft,
    isLimitLoading,
    onBackClick,
    isFromTolerance,
    isBudgetDataLoading,
    _budgetData,
    isArchived
  };
};

export default useLineItems;
